/* ==========================================================================
   Digitdeck / Millennio — El muelle de acordeon de la casa, ESTANDAR
   --------------------------------------------------------------------------
   #210 (Max, 2026-08-30): «todos los accordiones de la PDP deberian tener la
   misma animacion que tiene el contenedor de la garantia de devolucion, ya
   que esa es demasiado fluida y no tiene cortes en ningun lugar, y vuelvelo
   estandar para la pagina de milenio».

   La caja de garantia (`dd-promise-callout.js`) ya resolvia esto bien: anima
   la ALTURA con la Web Animations API en vez de dejar que <details> pliegue
   de golpe, e intercepta el cierre (nativamente solo se puede animar abrir).
   Otros acordeones de la tienda -el FAQ, sobre todo- usaban en cambio
   `::details-content` puramente en CSS, que en la practica se ve con
   pequeños cortes al interpolar hacia/desde `auto` en algunos motores. Este
   archivo es ESA MISMA logica -literal, no una version distinta-, generica
   para CUALQUIER <details> que lleve el atributo `data-mi-acordeon`, para
   que dejar de copiar y pegar el mismo script en cada seccion.

   Delegado en `document`, no por elemento: sobrevive a que el cart drawer se
   repinte por Section Rendering API o a que el editor recargue una seccion.
   Sin JS o sin WAAPI el <details> sigue funcionando, solo que el plegado es
   instantaneo -nunca deja de operar.
   ========================================================================== */

(function () {
  'use strict';

  var DURACION = 260;
  var CURVA = 'cubic-bezier(0.4, 0, 0.2, 1)';
  var enCurso = new WeakMap();

  function prefiereMenosMovimiento() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  // El panel es el primer hijo que NO es el <summary>: no hace falta que
  // cada componente le ponga una clase propia solo para que este script lo
  // encuentre.
  function panelDe(details) {
    for (var i = 0; i < details.children.length; i++) {
      var hijo = details.children[i];
      if (hijo.tagName !== 'SUMMARY') return hijo;
    }
    return null;
  }

  function curvaDe(details) {
    var cs = getComputedStyle(details);
    var dur = parseFloat(cs.getPropertyValue('--mi-acordeon-dur'));
    var easing = (cs.getPropertyValue('--mi-acordeon-curva') || '').trim();
    return {
      duracion: dur > 0 ? dur : DURACION,
      easing: easing || CURVA
    };
  }

  function animar(details, abriendo) {
    var panel = panelDe(details);
    if (!panel || typeof panel.animate !== 'function') {
      if (!abriendo) details.removeAttribute('open');
      details.classList.remove('mi-acordeon--cerrando');
      return;
    }

    var previa = enCurso.get(details);
    if (previa && previa.temporizador) clearTimeout(previa.temporizador);
    if (previa && previa.anim) previa.anim.cancel();
    if (previa && previa.animDet) previa.animDet.cancel();

    var alto = panel.scrollHeight;
    var c = curvaDe(details);
    panel.style.overflow = 'hidden';

    /* #215 (Max, 2026-08-30): «la animacion de los acordeones tiene un
       pequeño brinco al cerrar». Medido: el panel lleva `padding-bottom`
       CONDICIONADO a `[open]` (20px abierto, 0 cerrado) -una regla CSS
       aparte, que este script no tocaba-. Mientras `height` baja a 0 con
       el muelle, `[open]` sigue puesto (se quita al FINAL a proposito, ver
       `finalizar`), asi que el padding se queda en 20px durante TODA la
       animacion; con `box-sizing: border-box` el navegador no deja que la
       caja baje de su propio padding, asi que se queda clavada en ~20px
       hasta el ultimo frame -ahi se quita `[open]`, el padding cae a 0 de
       golpe, y ESE salto instantaneo es el brinco-. Animar `paddingBottom`
       (y `paddingTop`, por si algun acordeon futuro lo usa) EN EL MISMO
       `animate()` que la altura hace que los dos lleguen a 0 juntos, sin
       nada que "saltar" cuando `[open]` se quita despues. */
    var estilo = getComputedStyle(panel);
    var padArriba = estilo.paddingTop;
    var padAbajo = estilo.paddingBottom;

    var anim = panel.animate(
      [
        {
          height: (abriendo ? 0 : alto) + 'px',
          paddingTop: abriendo ? '0px' : padArriba,
          paddingBottom: abriendo ? '0px' : padAbajo,
          opacity: abriendo ? 0 : 1
        },
        {
          height: (abriendo ? alto : 0) + 'px',
          paddingTop: abriendo ? padArriba : '0px',
          paddingBottom: abriendo ? padAbajo : '0px',
          opacity: abriendo ? 1 : 0
        }
      ],
      {
        duration: c.duracion,
        easing: c.easing,
        // Al cerrar hay que sostener el estado final: sin esto el panel
        // vuelve a su alto natural un frame antes de que se quite [open].
        fill: abriendo ? 'none' : 'forwards'
      }
    );

    /* Algunos acordeones (el FAQ) ponen su padding condicionado a [open] en
       el propio <details>, no en el panel -medido: el mismo brinco del
       #215 seguia ahi con solo animar el panel-. Se anima aparte, EN
       PARALELO y con la misma curva, solo si de verdad tiene algo que
       animar. */
    var estiloDet = getComputedStyle(details);
    var detPadArriba = estiloDet.paddingTop;
    var detPadAbajo = estiloDet.paddingBottom;
    var animDet = null;
    if (parseFloat(detPadArriba) > 0 || parseFloat(detPadAbajo) > 0) {
      animDet = details.animate(
        [
          { paddingTop: abriendo ? '0px' : detPadArriba, paddingBottom: abriendo ? '0px' : detPadAbajo },
          { paddingTop: abriendo ? detPadArriba : '0px', paddingBottom: abriendo ? detPadAbajo : '0px' }
        ],
        { duration: c.duracion, easing: c.easing, fill: abriendo ? 'none' : 'forwards' }
      );
    }

    var cerrado = false;

    function finalizar() {
      if (cerrado) return;
      cerrado = true;

      var registro = enCurso.get(details);
      if (registro && registro.temporizador) clearTimeout(registro.temporizador);
      enCurso['delete'](details);

      if (!abriendo) details.removeAttribute('open');

      // Se cancela siempre, tambien al abrir: si el timeline va congelado
      // (pestaña en segundo plano, pane sin componer) la animacion seguiria
      // sujetando el panel a altura 0 con el <details> ya abierto.
      anim.cancel();
      if (animDet) animDet.cancel();

      panel.style.overflow = '';
      details.classList.remove('mi-acordeon--cerrando');
    }

    // Red de seguridad: si `onfinish` nunca llega, el temporizador cierra el
    // estado igual.
    var temporizador = setTimeout(finalizar, c.duracion + 120);
    enCurso.set(details, { anim: anim, animDet: animDet, temporizador: temporizador });
    anim.onfinish = finalizar;
    anim.oncancel = function () { panel.style.overflow = ''; };
  }

  document.addEventListener('click', function (evento) {
    if (!evento.target || !evento.target.closest) return;

    var cabecera = evento.target.closest('summary');
    if (!cabecera) return;

    var details = cabecera.parentElement;
    if (!details || !details.hasAttribute('data-mi-acordeon')) return;

    if (prefiereMenosMovimiento()) return; // toggle nativo, instantaneo

    evento.preventDefault();

    if (details.hasAttribute('open')) {
      details.classList.add('mi-acordeon--cerrando');
      animar(details, false);
    } else {
      details.setAttribute('open', '');
      animar(details, true);
    }
  });

  document.documentElement.classList.add('mi-acordeon-js');
})();
